import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  BulkDeleteVibeAppEnvVarsBodySchema,
  BulkDeleteVibeAppEnvVarsPathVarsSchema,
  BulkDeleteVibeAppEnvVarsResponseSchema,
  CreateVibeAppCardBindingBodySchema,
  CreateVibeAppCardBindingPathVarsSchema,
  CreateVibeAppCardBindingResponseSchema,
  DeleteVibeAppCardBindingPathVarsSchema,
  DeleteVibeAppCardBindingResponseSchema,
  DeleteVibeAppEnvVarPathVarsSchema,
  DeleteVibeAppEnvVarResponseSchema,
  GetVibeAppEnvironmentPathVarsSchema,
  GetVibeAppEnvironmentResponseSchema,
  GetVibeAppLogsPathVarsSchema,
  GetVibeAppLogsQuerySchema,
  GetVibeAppLogsResponseSchema,
  ListGrantableAccessCardsPathVarsSchema,
  ListGrantableAccessCardsResponseSchema,
  ListVibeAppEnvVarsPathVarsSchema,
  ListVibeAppEnvVarsWithCardsResponseSchema,
  UpdateVibeAppCardBindingBodySchema,
  UpdateVibeAppCardBindingPathVarsSchema,
  UpdateVibeAppCardBindingResponseSchema,
  UpsertVibeAppEnvVarBodySchema,
  UpsertVibeAppEnvVarPathVarsSchema,
  UpsertVibeAppEnvVarResponseSchema
} from "../schemas";

export const VibeEnvironmentEndpoints = {
  // ---------------------------------------------------------------------------
  // VibeAppEnvVar — plaintext env vars per app (secrets handled separately)
  // ---------------------------------------------------------------------------

  /**
   * List env vars for an app. Returns rows for all scopes; the caller
   * (CLI / build runner) decides which to apply per fleet. 404 if the
   * app is not in the caller's org.
   */
  ListEnvVars: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/env",
    PathVars: ListVibeAppEnvVarsPathVarsSchema,
    Response: ApiSuccessSchema(ListVibeAppEnvVarsWithCardsResponseSchema)
  },

  /**
   * Set an env var (`nexus app env set KEY=VALUE [--scope PROD]`).
   * Upserts on (vibeAppId, scope, name) — caller does not branch
   * create-vs-update. Defaults to scope=ALL when scope is omitted.
   * Returns the persisted row; 404 if the app is not in the caller's org.
   */
  UpsertEnvVar: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/env",
    PathVars: UpsertVibeAppEnvVarPathVarsSchema,
    Body: UpsertVibeAppEnvVarBodySchema,
    Response: ApiSuccessSchema(UpsertVibeAppEnvVarResponseSchema)
  },

  /**
   * Remove one env var by id. Hard delete — env vars carry no audit
   * value once removed; the git push history is the audit trail for
   * "what config did this build see?". 404 if the row is not owned
   * by the given app + org pair.
   */
  DeleteEnvVar: {
    method: "DELETE" as const,
    path: "/api/vibe/apps/:appId/env/:envVarId",
    PathVars: DeleteVibeAppEnvVarPathVarsSchema,
    Response: ApiSuccessSchema(DeleteVibeAppEnvVarResponseSchema)
  },

  /**
   * Remove a set of env vars in one call. Same hard-delete, no-audit
   * semantics as `DeleteEnvVar` — see its doc comment — just batched, so
   * the console's bulk-select bar fires one request and one confirm
   * instead of N. Ids outside this app + org are silently excluded from
   * the count rather than erroring the whole batch (mirrors `DeleteEnvVar`
   * mapping a not-found id to count=0, extended to a set).
   */
  BulkDeleteEnvVars: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/env/bulk/delete",
    PathVars: BulkDeleteVibeAppEnvVarsPathVarsSchema,
    Body: BulkDeleteVibeAppEnvVarsBodySchema,
    Response: ApiSuccessSchema(BulkDeleteVibeAppEnvVarsResponseSchema)
  },

  // ---------------------------------------------------------------------------
  // VibeAppCardBinding — access cards projected into an app's environment
  //
  // Console plane only. NONE of the four routes below carries `@PublicAPI` or
  // `@AllowOrgApiKey`, so `CompositeAuthGuard` falls through to `return false`
  // for every API-key request — a property of the route's SHAPE rather than of
  // a decorator anyone can forget to add. Importing a card delegates a human's
  // credential authority, and a machine key must not be able to do that.
  //
  // `ListEnvVars` above is the deliberate exception: it is a READ, it already
  // accepts an org API key, and the CLI needs card rows there or it prints an
  // environment that is missing entries.
  // ---------------------------------------------------------------------------

  /**
   * The app's environment as ONE merged, sorted list — literals, Vault
   * pointers and card bindings together.
   *
   * One endpoint rather than three, because the three kinds share one
   * namespace at the database level. A client that merged three arrays would
   * be inventing a resolution order, and an invented resolution order is the
   * shadowing bug `VibeAppEnvKey` exists to delete.
   *
   * No secrets in any branch: a literal's value is plaintext configuration, a
   * Vault pointer carries a path, and a card carries a handle.
   */
  GetAppEnvironment: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/environment",
    PathVars: GetVibeAppEnvironmentPathVarsSchema,
    Response: ApiSuccessSchema(GetVibeAppEnvironmentResponseSchema)
  },

  /**
   * One bounded page of an app's RUNTIME logs, newest first.
   *
   * Sibling of the build-log stream and deliberately a different shape: that
   * one follows a build for minutes and is therefore a raw SSE route outside
   * the generated surface, while this answers ONE page and fits the envelope.
   *
   * 🔴 THE GATEWAY HOST IS DERIVED FROM THE ORGANIZATION AND IS NEVER RECEIVED
   * IN A REQUEST FIELD. `appId` is resolved org-scoped to a `VibeApp`, that
   * app's `organizationId` is asserted against the JWT's org BEFORE any call
   * leaves the control plane, and only then is the org resolved to its
   * `VibeTenantCluster` and its logs host composed. There is no input a caller
   * can supply that changes which tenant is addressed — which is what makes a
   * cross-tenant read structurally impossible rather than merely forbidden.
   *
   * An `appId` belonging to another org returns 404, identical in body and in
   * path length to a genuinely missing app.
   */
  GetAppLogs: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/logs",
    PathVars: GetVibeAppLogsPathVarsSchema,
    Query: GetVibeAppLogsQuerySchema,
    Response: ApiSuccessSchema(GetVibeAppLogsResponseSchema)
  },

  /**
   * Cards the caller may import into this app.
   *
   * Mounted under the app rather than on `/api/access-cards`, and that is
   * load-bearing twice over. The answer depends on the app — an already-bound
   * card is flagged — so the app belongs in the path and not in a query
   * parameter. And `/api/access-cards/:id` is a live single-segment route, so
   * a sibling `/api/access-cards/grantable` would resolve by controller
   * registration ORDER: correct or shadowed depending on an import list in a
   * third file, and silently either way.
   */
  ListGrantableCards: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/environment/grantable-cards",
    PathVars: ListGrantableAccessCardsPathVarsSchema,
    Response: ApiSuccessSchema(ListGrantableAccessCardsResponseSchema)
  },

  /**
   * Import an access card into this app's environment under a name the author
   * picks. Creates the grant (the AUTHORITY) and the binding (its PROJECTION
   * into the environment screen) in one transaction.
   *
   * `projection` accepts only `HANDLE` today — every other delivery path puts
   * credential material at or near the app and ships behind its own
   * enforcement.
   */
  CreateCardBinding: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/env/card-bindings",
    PathVars: CreateVibeAppCardBindingPathVarsSchema,
    Body: CreateVibeAppCardBindingBodySchema,
    Response: ApiSuccessSchema(CreateVibeAppCardBindingResponseSchema)
  },

  /**
   * Rename a binding or move it between scopes. Re-reserves the environment
   * name, so a rename onto a name a literal or a Vault pointer already holds
   * is refused by the same constraint an import would hit.
   */
  UpdateCardBinding: {
    method: "PATCH" as const,
    path: "/api/vibe/apps/:appId/env/card-bindings/:bindingId",
    PathVars: UpdateVibeAppCardBindingPathVarsSchema,
    Body: UpdateVibeAppCardBindingBodySchema,
    Response: ApiSuccessSchema(UpdateVibeAppCardBindingResponseSchema)
  },

  /**
   * Remove a card from this app's environment.
   *
   * Hard-deletes the binding and RELEASES its environment name in the same
   * transaction — the namespace foreign key cascades parent to child and never
   * the other way, so a child removed without its parent leaves the name held
   * by nothing and refuses the next import for a name nobody owns.
   *
   * Removing the app's LAST binding on a card also revokes the grant, because
   * the authority existed to serve the projection and nothing is left to serve.
   */
  DeleteCardBinding: {
    method: "DELETE" as const,
    path: "/api/vibe/apps/:appId/env/card-bindings/:bindingId",
    PathVars: DeleteVibeAppCardBindingPathVarsSchema,
    Response: ApiSuccessSchema(DeleteVibeAppCardBindingResponseSchema)
  }
} as const;
